import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Download, Trash2, LogOut } from "lucide-react";
import { api, API, authHeaders, errText } from "@/lib/api";

const fmt = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const exportCsv = (rows) => {
  const csv = ["email,signed_up_at", ...rows.map((r) => `"${r.email.replace(/"/g, '""')}",${r.created_at}`)].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = `syndicate369-waitlist-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
};

export const VaultList = ({ email, onLogout }) => {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get(`${API}/admin/waitlist`, { headers: authHeaders() })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { if (e?.response?.status === 401) onLogout(); else { setRows([]); toast.error(errText(e)); } });
  }, [onLogout]);

  const shown = useMemo(() => (rows || []).filter((r) => r.email.includes(q.trim().toLowerCase())), [rows, q]);

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.email} from the list?`)) return;
    try {
      await api.delete(`${API}/admin/waitlist/${r.id}`, { headers: authHeaders() });
      setRows((p) => p.filter((x) => x.id !== r.id));
      toast.success("Removed.");
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="v-dash">
      <header className="v-top">
        <div className="v-brand"><img src="/media/eye-black.webp" alt="" /><span>Syndicate 369 · Vault</span></div>
        <button className="v-ghost" onClick={onLogout} data-testid="vault-logout-button"><LogOut size={14} /> <span>{email}</span></button>
      </header>

      <section className="v-head">
        <div>
          <p className="v-kicker">The list</p>
          <h2 data-testid="vault-count">{rows ? rows.length : "—"} <em>{rows?.length === 1 ? "name" : "names"}</em></h2>
        </div>
        <div className="v-tools">
          <label className="v-search"><Search size={15} />
            <input placeholder="Search emails" value={q} onChange={(e) => setQ(e.target.value)} data-testid="vault-search-input" />
          </label>
          <button className="v-solid" onClick={() => exportCsv(shown)} disabled={!shown.length} data-testid="vault-export-button">
            <Download size={14} /> CSV
          </button>
        </div>
      </section>

      <ol className="v-rows" data-testid="vault-list">
        {rows && !shown.length && <li className="v-empty" data-testid="vault-empty">{q ? "No email matches that." : "No one yet. They will come."}</li>}
        {shown.map((r, i) => (
          <li key={r.id} className="v-row" data-testid={`vault-row-${r.id}`}>
            <span className="v-n">{String(shown.length - i).padStart(3, "0")}</span>
            <span className="v-mail">{r.email}</span>
            <span className="v-date">{fmt(r.created_at)}</span>
            <button className="v-del" onClick={() => remove(r)} aria-label={`Delete ${r.email}`} data-testid={`vault-delete-${r.id}`}><Trash2 size={15} /></button>
          </li>
        ))}
      </ol>
    </div>
  );
};
